import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { NotificationKey, StatusCode } from 'helpers/app.config';
import { KEYS } from 'helpers/react-query.config';

export interface DestinyStatus {
  code: StatusCode;
  message?: string;
  title?: string;
  acceptButtonText?: string;
  commentTitle?: string;
}

interface Props {
  notificationId?: number;
  ticketId?: number;
  ticketActive?: string;
  destinyStatus?: DestinyStatus;
  setDestinyStatus?: React.Dispatch<React.SetStateAction<DestinyStatus>>;
  setTicketId?: React.Dispatch<React.SetStateAction<number>>;
  setNotificationId?: React.Dispatch<React.SetStateAction<number>>;
  handleClose?: () => void;
  handleReadNotification?: (notificationId: number) => void;
}

/**
 * Return type for the `useNotificationActions` hook.
 */
export type NotificationActionsReturn = {
  /**
   * Dispatches an action based on the notification key and user decision.
   * Used to trigger different flows depending on notification type.
   *
   * @param notificationKey - Key identifying the notification type.
   * @param accept - Whether the user accepted (true) or rejected (false) the action.
   */
  dispatchAction: (notificationKey: NotificationKey, accept: boolean) => void;

  /**
   * Current destiny status related to the notification action.
   */
  destinyStatus?: DestinyStatus;

  /**
   * Updates the destiny status.
   */
  setDestinyStatus?: React.Dispatch<React.SetStateAction<DestinyStatus>>;
};

/**
 * Custom hook to manage notification actions related to ticket status updates.
 *
 * @param {Object} props - The properties for the hook.
 * @param {StatusCode} props.destinyStatus - The current status of the destiny.
 * @param {Function} props.handleClose - Function to handle closing the notification.
 * @param {string} props.notificationId - The ID of the notification.
 * @param {Function} props.setDestinyStatus - Function to set the destiny status.
 * @param {Function} props.setNotificationId - Function to set the notification ID.
 * @param {Function} props.setTicketId - Function to set the ticket ID.
 * @param {string} props.ticketId - The ID of the ticket.
 * @param {Function} props.handleReadNotification - Function to handle reading the notification.
 *
 *
 * @type {Object} NotificationActionsReturn
 * @property {Function} dispatchAction - Dispatches the appropriate action based on the notification key and user decision.
 * @property {DestinyStatus} destinyStatus - Current destiny status information shown to the user.
 * @property {Function} setDestinyStatus - Function to update the destiny status state.
 *
 * @returns {NotificationActionsReturn}
 */

const useNotificationActions = ({
  destinyStatus,
  handleClose,
  notificationId,
  setDestinyStatus,
  setNotificationId,
  setTicketId,
  ticketId,
  handleReadNotification
}: Props): NotificationActionsReturn => {
  const { ticket: ticketApi } = useApiContext();
  const { showApiError, showSuccess } = useGlobalContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');
  const updateTicketToPendingWarehouseStatus = useMutation(
    (warehouseRevisionApproved: boolean) =>
      ticketApi.tickets.updateTicketToPendingWarehouseStatus({
        ticketId,
        comment: '',
        latitude: 0,
        longitude: 0,
        warehouseRevisionApproved
      }),
    {
      onSuccess: async (data) => {
        handleReadNotification(notificationId);

        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(+data.data.data.ticketId)
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          ),
          queryClient.invalidateQueries(KEYS.ticket.tickets.generalSearch())
        ]);

        showSuccess(
          `${t('ticket.changeStatus.updatedTicketSuccess')} ${
            data.data.data.ticketId
          }`
        );
      },
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
        handleClose();
      }
    }
  );

  const updateTicketToPendingforSerialStatus = useMutation(
    () =>
      ticketApi.tickets.updateTicketToPendingForSerieStatus({
        ticketId,
        comment: ''
      }),
    {
      onSuccess: async (data) => {
        handleReadNotification(notificationId);

        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(+data.data.data.ticketId)
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          ),
          queryClient.invalidateQueries(KEYS.ticket.tickets.generalSearch())
        ]);

        showSuccess(
          `${t('ticket.changeStatus.updatedTicketSuccess')} ${
            data.data.data.ticketId
          }`
        );
      },
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
        handleClose();
      }
    }
  );

  const updateTicketToScheduled = useMutation(
    ({
      comment,
      scheduledApproved
    }: {
      comment: string;
      scheduledApproved: boolean;
    }) =>
      ticketApi.tickets.meUpdateTicketsToSchedueled({
        TicketId: String(ticketId),
        comment,
        scheduledApproved
      }),
    {
      onSuccess: async (data) => {
        handleReadNotification(notificationId);

        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(+data.data.data.ticketId)
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          ),
          queryClient.invalidateQueries(KEYS.ticket.tickets.generalSearch())
        ]);

        showSuccess(
          `${t('ticket.changeStatus.updatedTicketSuccess')} ${
            data.data.data.ticketId
          }`
        );
      },
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
        handleClose();
      }
    }
  );

  const dispatchAction = (
    notificationKey: NotificationKey,
    accept: boolean
  ) => {
    switch (notificationKey) {
      case NotificationKey.TicketReviewPendingSerie:
        if (accept) {
          setNotificationId(notificationId);

          updateTicketToPendingforSerialStatus.mutate();
        } else {
          setNotificationId(notificationId);
          setDestinyStatus({
            code: StatusCode.Working,
            message: t('ticket.changeStatus.messageRejectSerialPendig'),
            title: t('ticket.changeStatus.titleModalRejectSerialStatusChange'),
            acceptButtonText: t('common.commonActions.accept'),
            commentTitle: `* ${t('common.commonProperties.comment')}`
          });
          setTicketId(ticketId);
          handleClose();
        }

        break;
      case NotificationKey.ReviewPendingWarehouse:
        setNotificationId(notificationId);
        updateTicketToPendingWarehouseStatus.mutate(accept);

        break;
      case NotificationKey.ReviewScheduled:
        if (accept) {
          setNotificationId(notificationId);
          updateTicketToScheduled.mutate({
            comment: '',
            scheduledApproved: true
          });
        } else {
          setNotificationId(notificationId);
          setDestinyStatus({
            code: StatusCode.Scheduled,
            message: t('ticket.changeStatus.messageRejectSchedule'),
            title: t(
              'ticket.changeStatus.titleModalRejectsScheduleStatusChange'
            ),
            acceptButtonText: t('common.commonActions.accept'),
            commentTitle: `* ${t('common.commonProperties.comment')}`
          });
          setTicketId(ticketId);
          handleClose();
        }
        break;
      default:
        showApiError(null, t('common.commonMessages.noAction'), 3000);
        break;
    }
  };

  return {
    dispatchAction,
    destinyStatus,
    setDestinyStatus
  };
};

export default useNotificationActions;
